import type { FC, UIEvent as ReactUIEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, CircularProgress, IconButton, Stack, Tooltip, useTheme } from '@mui/material';

import { useTrackedPage } from 'analytics/appInsights';
import { useNodeDetail } from 'api/data/hooks/useNodeDetail';
import { flattenNodeDetailProperties } from 'api/data/projects/types';
import { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import { isOwnershipSettings } from 'api/meta/enums/nodes/properties/EPropertySettingUtils';
import { NODE_DETAILS_PANEL_WIDTH } from 'app/AppConstants';
import { useDelayedFlag } from 'hooks/useDelayedFlag';
import { NodeTitle } from 'pages/projects/projectPage/selectedProject/nodeDetails/header/NodeTitle';
import type { PropertyValue } from 'state/data/dataState';
import { useSelectedProjectTemplateNode } from 'state/selectedProject/selectedProjectHooks';

import { useNodeAvailability } from '../hooks/useNodeAvailability';
import { useUpdateNodeDetailProperty } from '../hooks/useUpdateNodeDetailProperty';
import { NODE_DETAILS_TEST_ID } from '../testIds';
import { NodeDetailsHeaderId } from './NodeDetailsHeaderId';
import { NodeDetailsProperty } from './NodeDetailsProperty';

const STALE_LOADING_DELAY_MS = 300;
const HEADER_SPINNER_SIZE = 20;

export interface NodeDetailsPanelProps {
  projectId: string;
  templateNodeId: string;
  dataNodeId: string;
  proposalId?: string;
  /** Shows values without allowing edits, e.g. on the Audit page. */
  readOnly?: boolean;

  onClose: () => void;
  /** Called instead of `onClose` when the node is gone (see {@link useNodeAvailability}). */
  onUnavailable: () => void;
}

/**
 * Single node details panel decoupled from the grid data layer (GRID_STORE).
 * Rendered inline in a page flex layout (NewTreeView / hierarchy view).
 *
 * Property values are read directly from the `useNodeDetail` React Query and
 * written back via {@link useUpdateNodeDetailProperty}. Tree labels update via
 * SignalR `DataNodeUpdated`. Unlike the legacy grid panel, this has no
 * Views/Attachments tabs and no fullscreen mode. Layout mirrors the legacy
 * panel otherwise: the title and primary property stay fixed, and the
 * remaining properties scroll below them.
 */
export const NodeDetailsPanel: FC<NodeDetailsPanelProps> = ({
  projectId,
  templateNodeId,
  dataNodeId,
  proposalId,
  readOnly = false,
  onClose,
  onUnavailable
}) => {
  const theme = useTheme();

  useTrackedPage(`${projectId}_${templateNodeId}_${dataNodeId}`, 'Node Detail Panel', {
    projectId,
    templateNodeId,
    dataNodeId
  });

  const { data, isPending, isPlaceholderData } = useNodeDetail(projectId, templateNodeId, dataNodeId, proposalId);
  // Not `isFetching`: a background refetch of the node already on screen must not dim it.
  const isStale = isPending || isPlaceholderData;
  // Delayed so a fast load doesn't flash the spinner — but fields must disable immediately (below),
  // or a previous node's placeholder data stays editable for the length of this delay.
  const showStaleLoading = useDelayedFlag(isStale, STALE_LOADING_DELAY_MS, dataNodeId);
  const isDisabled = isStale || readOnly;

  const templateNode = useSelectedProjectTemplateNode(templateNodeId);

  const properties = useMemo<Record<string, PropertyValue>>(
    () => (data ? flattenNodeDetailProperties(data) : {}),
    [data]
  );

  const primaryPropertyId = templateNode?.primaryPropertyId ?? '';

  const update = useUpdateNodeDetailProperty(projectId, templateNodeId, dataNodeId, proposalId);

  const { ownershipProperty, primaryProperty, listedProperties } = useMemo(() => {
    const props = templateNode?.properties ?? [];

    return {
      ownershipProperty: props.find((p) => isOwnershipSettings(p.settings)),
      primaryProperty: primaryPropertyId ? props.find((p) => p.id === primaryPropertyId) : undefined,
      listedProperties: !primaryPropertyId
        ? []
        : props
            .filter((p) => p.id !== primaryPropertyId && p.settings.$type !== EPropertySettingTypes.Ownership)
            .sort((a, b) => a.index - b.index)
    };
  }, [templateNode, primaryPropertyId]);

  const handleKeyDown = useCallback(
    (event: { key: string; stopPropagation: () => void }) => {
      if (event.key === 'Escape') {
        // The page's own Escape handler would otherwise close it again, adding a second history entry.
        event.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );

  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback((event: ReactUIEvent<HTMLDivElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  }, []);

  const { isShown } = useNodeAvailability(projectId, templateNodeId, dataNodeId, proposalId, onUnavailable);

  if (!isShown) return null;

  return (
    <Stack
      data-testid={NODE_DETAILS_TEST_ID.panel}
      id='tree-node-details-panel'
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        flexShrink: 0,
        width: NODE_DETAILS_PANEL_WIDTH,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'common.white',
        borderLeft: `1px solid ${theme.palette.divider}`
      }}
    >
      <Stack id='node-details-header' padding={theme.spacing(2, 2, 0.5, 2)}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <NodeDetailsHeaderId templateNodeId={templateNodeId} properties={properties} />

          <Stack direction='row' justifyContent='flex-end' alignItems='center' spacing={1}>
            <Box
              sx={{
                width: HEADER_SPINNER_SIZE,
                height: HEADER_SPINNER_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {showStaleLoading && (
                <CircularProgress size={HEADER_SPINNER_SIZE} data-testid={NODE_DETAILS_TEST_ID.loading} />
              )}
            </Box>
            {ownershipProperty && (
              <NodeDetailsProperty
                key={`${templateNodeId}-${dataNodeId}-${ownershipProperty.id}`}
                templateNodeId={templateNodeId}
                property={ownershipProperty}
                value={properties[ownershipProperty.technicalName] ?? null}
                onUpdate={update}
                disabled={isDisabled}
              />
            )}

            {readOnly && (
              <Tooltip title='Read-only'>
                <LockOutlinedIcon color='action' data-testid={NODE_DETAILS_TEST_ID.readOnlyIcon} />
              </Tooltip>
            )}

            <Tooltip title='Close (Esc)'>
              <IconButton onClick={onClose} data-testid={NODE_DETAILS_TEST_ID.closeButton}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      <Stack id='node-details-content' direction='column' overflow='hidden' flex={1}>
        <Stack
          id='node-details-fields'
          padding={theme.spacing(0, 3, 2, 3)}
          width={1}
          overflow='hidden'
          flexShrink={0}
          sx={{
            // Divider only once the properties scroll under the fields; transparent at rest so the height stays put.
            borderBottom: 1,
            borderColor: isScrolled ? 'divider' : 'transparent',
            transition: theme.transitions.create('border-color'),
            zIndex: 1
          }}
        >
          <Stack id='node-details-fields-list' spacing={2}>
            <NodeTitle templateNodeId={templateNodeId} />

            {primaryProperty && (
              <NodeDetailsProperty
                key={`${templateNodeId}-${dataNodeId}-${primaryProperty.id}`}
                templateNodeId={templateNodeId}
                property={primaryProperty}
                value={properties[primaryProperty.technicalName] ?? null}
                onUpdate={update}
                disabled={isDisabled}
              />
            )}
          </Stack>
        </Stack>

        <Stack id='node-details-properties' onScroll={handleScroll} overflow='auto' flex={1}>
          <Stack id='node-details-properties-list' spacing={1} padding={theme.spacing(2, 3, 3)}>
            {listedProperties.map((property, index) => (
              <Stack
                key={`${templateNodeId}-${dataNodeId}-${property.id}`}
                data-testid={NODE_DETAILS_TEST_ID.property(index)}
              >
                <NodeDetailsProperty
                  templateNodeId={templateNodeId}
                  property={property}
                  value={properties[property.technicalName] ?? null}
                  onUpdate={update}
                  disabled={isDisabled}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
